import React, { useState } from 'react';
import { apiFetch } from '../../config/api';
import { priceIdFor, MESES_GRATIS_NO_ANUAL, mensalEquivalente } from '../../config/stripePrices';
import { usePlanos } from '../../config/planos';
import { useIdioma } from '../../i18n';
import { useFormato } from '../../i18n/formato';
import '../../styles/Degraus.css';

/**
 * Preços como uma escada: quatro planos, um degrau cada, o mais alto à direita.
 * Os limites e os preços vêm de `usePlanos` (planConfigs + stripePrices), como
 * na tela de planos. Não há selo de "mais popular": nenhum dado o sustenta.
 *
 * O id continua `pricing` porque o Header e o Footer apontam para `/#pricing`.
 */
const Degraus = () => {
  const { t } = useIdioma();
  const { numero, locale } = useFormato();
  const planos = usePlanos();
  const [anual, setAnual] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');

  /* shortcut: cópia da lógica de compra de components/Pricing.js, que segue
     servindo a Home antiga enquanto ela existir; ao apagá-la, esta fica sendo
     a única. Mudou a rota ou o corpo do checkout? Mude as duas. */
  const irParaContato = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const escolherPlano = async (plano) => {
    if (plano.contato) return irParaContato();
    if (carregando) return;

    const priceId = priceIdFor(plano.nome, anual ? 'annual' : 'monthly');
    if (!priceId) {
      setErro(t('erros.plano'));
      return;
    }

    setCarregando(true);
    setErro('');

    try {
      const resposta = await apiFetch('/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ priceId, fromPlans: true })
      });

      const dados = await resposta.json();

      if (resposta.ok && dados.url) {
        window.location.href = dados.url;
      } else {
        setErro(dados.error || t('erros.requisicao'));
      }
    } catch (e) {
      setErro(t('erros.generico'));
    } finally {
      setCarregando(false);
    }
  };

  /* Em dólar em todo idioma; o locale decide só a pontuação e onde o símbolo entra. */
  const dolar = (valor, casas = 0) =>
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: casas,
      maximumFractionDigits: casas
    }).format(valor);

  const preco = (p) => {
    if (p.contato) return { valor: t('precos.personalizado') };
    if (p.preco.mensal === 0) return { valor: dolar(0), detalhe: t('inicio.degraus.paraSempre') };
    if (anual) {
      return {
        valor: dolar(mensalEquivalente(p.nome), 2),
        detalhe: t('precos.anualDetalhe', { valor: numero(p.preco.anual) })
      };
    }
    return { valor: dolar(p.preco.mensal), detalhe: t('precos.porMes') };
  };

  const acao = (p) => {
    if (carregando && !p.contato) return t('precos.carregando');
    if (p.contato) return t('inicio.degraus.falar');
    if (p.preco.mensal === 0) return t('inicio.degraus.comecarGratis');
    return t('inicio.degraus.assinar', { plano: p.nome });
  };

  return (
    <section className="degraus" id="pricing" aria-labelledby="degraus-titulo">
      <div className="degraus-escada">
        <div className="degraus-topo">
          <h2 id="degraus-titulo" data-r>{t('inicio.degraus.titulo')}</h2>
          <div className="degraus-ciclo" role="group" aria-label={t('precos.ciclo')}>
            <button type="button" aria-pressed={!anual} onClick={() => setAnual(false)}>
              {t('precos.mensal')}
            </button>
            <button type="button" aria-pressed={anual} onClick={() => setAnual(true)}>
              {t('inicio.degraus.anual', { n: MESES_GRATIS_NO_ANUAL })}
            </button>
          </div>
          {erro && <p className="degraus-erro" role="alert">{erro}</p>}
        </div>

        {planos.map((p, i) => {
          const { valor, detalhe } = preco(p);
          return (
            <article className={`degraus-plano degraus-${i + 1}`} key={p.nome} data-r style={{ '--i': i + 1 }}>
              <div className="degraus-corpo">
                <h3>{p.nome}</h3>
                <p className="degraus-preco">
                  <b className="nh-num">{valor}</b>
                  {detalhe && <span>{detalhe}</span>}
                </p>
                <p className="degraus-publico">{t(`inicio.degraus.publico.${p.nome}`)}</p>
                <ul>
                  {p.recursos.map((r) => (
                    <li key={r}>{r}</li>
                  ))}
                </ul>
              </div>
              <button
                type="button"
                className="degraus-acao"
                onClick={() => escolherPlano(p)}
                disabled={carregando && !p.contato}
              >
                {acao(p)} <span className="nh-seta" aria-hidden="true">→</span>
              </button>
            </article>
          );
        })}
      </div>
    </section>
  );
};

export default Degraus;
